import React from 'react';
import { Activity, ThermometerSun, Sun } from 'lucide-react';

/**
 * Компонент для ввода уровня активности, климата и сезона
 */
const ActivityAndEnvironmentSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  activityLevels,
  climateTypes,
  seasons
}) => {
  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Activity className="w-5 h-5 mr-2 text-blue-500" />
        Активность и окружающая среда
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Уровень активности */}
        <div className="col-span-full">
          <label htmlFor="activityLevel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Уровень физической активности
          </label>
          <select
            id="activityLevel"
            name="activityLevel"
            value={formData.activityLevel}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.activityLevel ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите уровень активности</option>
            {activityLevels.map(level => (
              <option key={level.id} value={level.id}>
                {level.name} ({level.description})
              </option>
            ))}
          </select>
          {errors.activityLevel && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.activityLevel}
            </p>
          )}
        </div>

        {/* Климат */}
        <div>
          <label htmlFor="climate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ThermometerSun className="w-4 h-4 mr-2 text-blue-500" />
            Климат
          </label>
          <select
            id="climate"
            name="climate"
            value={formData.climate}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.climate ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите климат</option>
            {climateTypes.map(climate => (
              <option key={climate.id} value={climate.id}>
                {climate.name} ({climate.description})
              </option>
            ))}
          </select>
          {errors.climate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.climate}
            </p>
          )}
        </div>

        {/* Сезон */}
        <div>
          <label htmlFor="season" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Sun className="w-4 h-4 mr-2 text-blue-500" />
            Сезон
          </label>
          <select
            id="season"
            name="season"
            value={formData.season}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.season ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите сезон</option>
            {seasons.map(season => (
              <option key={season.id} value={season.id}>
                {season.name}
              </option>
            ))}
          </select>
          {errors.season && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.season}
            </p>
          )}
        </div>

        {/* Диетические предпочтения */}
        <div>
          <label htmlFor="dietaryPreferences" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Диетические предпочтения
          </label>
          <select
            id="dietaryPreferences"
            name="dietaryPreferences"
            value={formData.dietaryPreferences}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="standard">Стандартное питание</option>
            <option value="high_protein">Высокобелковая диета</option>
            <option value="vegetarian">Вегетарианство</option>
            <option value="vegan">Веганство</option>
          </select>
        </div>

        {/* Потребление кофеина */}
        <div>
          <label htmlFor="caffeineConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Потребление кофеина
          </label>
          <select
            id="caffeineConsumption"
            name="caffeineConsumption"
            value={formData.caffeineConsumption}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="none">Не употребляю</option>
            <option value="low">Низкое (1 чашка кофе/чая в день)</option>
            <option value="moderate">Среднее (2-3 чашки в день)</option>
            <option value="high">Высокое (4+ чашки в день)</option>
          </select>
        </div>

        {/* Потребление алкоголя */}
        <div>
          <label htmlFor="alcoholConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Потребление алкоголя
          </label>
          <select
            id="alcoholConsumption"
            name="alcoholConsumption"
            value={formData.alcoholConsumption}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="none">Не употребляю</option>
            <option value="low">Редко (несколько раз в месяц)</option>
            <option value="moderate">Умеренно (1-2 раза в неделю)</option>
            <option value="high">Регулярно (3+ раза в неделю)</option>
          </select>
        </div>
      </div>

      <div className="mt-4 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start">
          <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Важно знать о влиянии активности на потребление воды
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              При высокой физической активности потребность в воде значительно увеличивается. 
              Рекомендуется дополнительно выпивать 300-500 мл воды до тренировки и примерно 
              200-300 мл каждые 20-30 минут во время интенсивной активности.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityAndEnvironmentSection;